import { useUserExportPresets } from '@/lib/use-user-export-presets';
import { cn } from '@/lib/utils';
import {
  EXPORT_PRESETS,
  applyExportPresetKeepingArrangement,
  matchAnyExportPreset,
} from '@mss_creator/shared';
import type {
  ActiveExportPreset,
  ExportPresetDefinition,
  ExportPresetSettings,
  UserExportPreset,
} from '@mss_creator/shared';
import { Badge, Button } from '@pms-digital/design-system';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { ExportPresetGlyph } from './export-preset-glyph';
import { ExportPresetSaveForm } from './export-preset-save-form';
import { ExportUserPresetRow } from './export-user-preset-row';

interface ExportPresetPickerProps {
  /** The panel's current option blocks. */
  options: ExportPresetSettings;
  /** Replaces all four blocks at once — a preset is a full snapshot. */
  onApply: (next: ExportPresetSettings) => void;
}

/** The preset last applied from this picker, which names the « · modifié » badge. */
type LastApplied =
  | { kind: 'builtin'; id: ExportPresetDefinition['id']; label: string }
  | { kind: 'user'; id: string; label: string };

/** The badge label for a matched preset, or null when the match names no known preset. */
function lastAppliedFor(
  active: NonNullable<ActiveExportPreset>,
  presets: readonly UserExportPreset[],
): LastApplied | null {
  if (active.kind === 'builtin') {
    const builtin = EXPORT_PRESETS.find((preset) => preset.id === active.id);
    return builtin ? { kind: 'builtin', id: builtin.id, label: builtin.label } : null;
  }
  const user = presets.find((preset) => preset.id === active.id);
  return user ? { kind: 'user', id: user.id, label: user.name } : null;
}

/**
 * A personal preset's four blocks, keeping the table's matrix-specific
 * arrangement (widths, column order, page breaks) — the same rule as the
 * built-ins' `applyExportPresetKeepingArrangement` (issue #11).
 */
function applyUserPresetKeepingArrangement(
  preset: UserExportPreset,
  current: ExportPresetSettings,
): ExportPresetSettings {
  const applied = structuredClone(preset.settings);
  const { columnWidths, columnOrder, pageBreaksBefore } = structuredClone(current.table);
  return { ...applied, table: { ...applied.table, columnWidths, columnOrder, pageBreaksBefore } };
}

/**
 * The export panel's entry point — export-rework chantier 2 (spec P1-P2) with
 * chantier 5's personal presets (issue #13, spec P3/E4): the four built-in
 * thumbnail cards, then the user's own presets as full-width ⭐ rows. The
 * active preset is derived (`matchAnyExportPreset`), never stored, so a tweak
 * that lands back on a preset re-activates it. Off every preset, a badge reads
 * « ‹dernier preset appliqué› · modifié » — the first preset matched on this
 * mount counts as applied — or « Personnalisé » if the options never matched
 * one, and « Enregistrer comme preset… » opens the save form.
 *
 * The picker owns the personal-presets query so the route only hands it the
 * options and a setter.
 */
export function ExportPresetPicker({ options, onApply }: ExportPresetPickerProps) {
  const { presets, isError, save, remove, isSaving } = useUserExportPresets();
  const [lastApplied, setLastApplied] = useState<LastApplied | null>(null);
  const [saving, setSaving] = useState(false);
  const headingId = useId();
  const unavailableId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const saveLinkRef = useRef<HTMLButtonElement>(null);
  /**
   * Set when the save form closes: focus goes back to « Enregistrer comme
   * preset… », or — after a save that made the new preset active, which hides
   * that link — to the new preset's row.
   */
  const focusAfterForm = useRef<{ savedId: string | null } | null>(null);

  useLayoutEffect(() => {
    const pending = focusAfterForm.current;
    if (pending === null) return;
    focusAfterForm.current = null;
    const savedRow =
      pending.savedId === null
        ? null
        : rootRef.current?.querySelector<HTMLButtonElement>(
            `[data-preset-id="${pending.savedId}"]`,
          );
    (saveLinkRef.current ?? savedRow)?.focus();
  });

  const active = matchAnyExportPreset(options, presets);
  // Until a preset is applied here, the first one the options match names the
  // badge — the entry state is Lecture, so a first tweak reads « Lecture ·
  // modifié » rather than « Personnalisé ». Adjusting state during render is
  // React's pattern for state derived from a previous render.
  if (lastApplied === null && active !== null) {
    const seed = lastAppliedFor(active, presets);
    if (seed !== null) setLastApplied(seed);
  }
  // The form only shows off every preset. Landing on one closes it for good,
  // so a later divergence shows the link again rather than the stale form.
  if (saving && active !== null) {
    setSaving(false);
  }
  // A deleted personal preset no longer names the badge or pre-fills the form.
  const lastAppliedLive =
    lastApplied?.kind === 'user' && !presets.some((preset) => preset.id === lastApplied.id)
      ? null
      : lastApplied;

  function applyBuiltin(preset: ExportPresetDefinition) {
    setLastApplied({ kind: 'builtin', id: preset.id, label: preset.label });
    onApply(applyExportPresetKeepingArrangement(preset.id, options.table));
  }

  function applyUser(preset: UserExportPreset) {
    setLastApplied({ kind: 'user', id: preset.id, label: preset.name });
    onApply(applyUserPresetKeepingArrangement(preset, options));
  }

  return (
    <div ref={rootRef} className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {EXPORT_PRESETS.map((preset) => {
          const isActive = active?.kind === 'builtin' && active.id === preset.id;
          return (
            <button
              key={preset.id}
              type="button"
              aria-pressed={isActive}
              onClick={() => applyBuiltin(preset)}
              className={cn(
                'flex flex-col items-stretch gap-1 rounded-md border-2 bg-white p-2 text-left transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit',
                isActive
                  ? 'border-bleu-nuit ring-1 ring-bleu-nuit'
                  : 'border-gray-200 hover:bg-gray-50',
              )}
            >
              <ExportPresetGlyph presetId={preset.id} />
              <span className="font-semibold text-gray-900 text-small">{preset.label}</span>
              <span className="text-caption text-gray-500">{preset.hint}</span>
            </button>
          );
        })}
      </div>

      {presets.length > 0 && (
        <section aria-labelledby={headingId} className="space-y-1">
          <h4
            id={headingId}
            className="font-semibold text-caption text-gray-500 uppercase tracking-wide"
          >
            Mes presets
          </h4>
          <ul className="max-h-48 space-y-1 overflow-y-auto">
            {presets.map((preset) => (
              <ExportUserPresetRow
                key={preset.id}
                preset={preset}
                active={active?.kind === 'user' && active.id === preset.id}
                onApply={() => applyUser(preset)}
                onDelete={() => remove(preset.id)}
              />
            ))}
          </ul>
        </section>
      )}
      {isError && (
        <p id={unavailableId} className="text-caption text-gray-500">
          Presets personnels indisponibles — enregistrement impossible pour l'instant
        </p>
      )}

      {active === null && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Badge tone="neutral" maxWidth={200}>
            {lastAppliedLive === null ? 'Personnalisé' : `${lastAppliedLive.label} · modifié`}
          </Badge>
          {!saving && (
            <Button
              ref={saveLinkRef}
              type="button"
              variant="ghost"
              size="xs"
              icon="bookmark_add"
              onClick={() => setSaving(true)}
              // Without the list, the overwrite warning and the cap check are blind.
              disabled={isError}
              aria-describedby={isError ? unavailableId : undefined}
            >
              Enregistrer comme preset…
            </Button>
          )}
        </div>
      )}
      {active === null && saving && (
        <ExportPresetSaveForm
          options={options}
          presets={presets}
          initialName={lastAppliedLive?.kind === 'user' ? lastAppliedLive.label : ''}
          save={save}
          isSaving={isSaving}
          listUnavailable={isError}
          onSaved={(saved) => {
            focusAfterForm.current = { savedId: saved.id };
            setLastApplied({ kind: 'user', id: saved.id, label: saved.name });
            setSaving(false);
          }}
          onCancel={() => {
            focusAfterForm.current = { savedId: null };
            setSaving(false);
          }}
        />
      )}
    </div>
  );
}
